import { TEST_IDS } from '@argus-labs/auth-shared/test-ids'
import { toast as sonnerToast } from 'sonner'
import { XIcon } from '@/components/decorative/x-icon'
import { Text } from '@/components/ui/text'

interface ToastProps {
  id: string | number
  description: string
  errorCode?: string
}

/**
 * Customize Sonner toast to be our own
 */
function toast(toast: Omit<ToastProps, 'id'>) {
  return sonnerToast.custom(
    (id) => <Toast id={id} description={toast.description} errorCode={toast.errorCode} />,
    { testId: TEST_IDS.toast },
  )
}

/**
 * Our toast that accepts `error` object from Better Auth API calls
 */
toast.fromError = (
  error: {
    code?: string | undefined
    message?: string | undefined
    status: number
    statusText: string
  } | null,
) => {
  if (!error) return
  return toast({ description: error.message ?? 'Unknown error', errorCode: error.code })
}

const PRESETS = {
  NETWORK_ERROR: {
    description: 'Failed to reach the server. Please try again.',
    errorCode: 'NETWORK_ERROR',
  },
} as const satisfies Record<string, { description: string; errorCode?: string }>

/**
 * Our toast with preset description and error code
 */
toast.fromPreset = (preset: keyof typeof PRESETS) => {
  return toast({
    description: PRESETS[preset].description,
    errorCode: PRESETS[preset].errorCode,
  })
}

export { toast }

function Toast(props: ToastProps) {
  const { description, errorCode, id } = props

  return (
    <div className="px-4 md:w-80">
      <div className="rounded-lg bg-orange flex-1 flex justify-between">
        <div className="pl-4 py-3 flex flex-1">
          <div className="w-full">
            <Text font="beausite" size={14} color="cream">
              {description}
              {errorCode ? (
                <>
                  <br />
                  Error code: {errorCode}
                </>
              ) : null}
            </Text>
          </div>
        </div>

        <button
          type="button"
          className="shrink-0 px-4 flex items-center justify-center cursor-pointer"
          onClick={() => {
            sonnerToast.dismiss(id)
          }}
        >
          <XIcon className="size-6" />
        </button>
      </div>
    </div>
  )
}
